import { TopNav } from "@dashboard/components/AppLayout/TopNav";
import { DashboardCard } from "@dashboard/components/Card";
import { ListPageLayout } from "@dashboard/components/Layouts";
import ResponsiveTable from "@dashboard/components/ResponsiveTable";
import TableRowLink from "@dashboard/components/TableRowLink";
import { WsmFitmentAxisFragment, WsmFitmentValueKind } from "@dashboard/graphql";
import useNavigator from "@dashboard/hooks/useNavigator";
import { renderCollection } from "@dashboard/misc";
import { TableBody, TableCell, TableHead } from "@material-ui/core";
import { Button, Skeleton, Text } from "@saleor/macaw-ui-next";
import React from "react";
import { useIntl } from "react-intl";

import { axisMessages, sectionMessages } from "../messages";
import { wsmFitmentAxisAddPath, wsmFitmentAxisUrl } from "../urls";
import { WsmFitmentLoadError } from "./WsmFitmentLoadError";
import { WsmFitmentSectionLinks } from "./WsmFitmentSectionLinks";

const numberOfColumns = 3;

export interface WsmFitmentAxisListPageProps {
  axes: WsmFitmentAxisFragment[] | undefined;
  /** Set when the list failed to load: the card shows a retry instead of rows. */
  onRetry?: () => void;
}

export const WsmFitmentAxisListPage = ({ axes, onRetry }: WsmFitmentAxisListPageProps) => {
  const intl = useIntl();
  const navigate = useNavigator();

  return (
    <ListPageLayout>
      <TopNav title={intl.formatMessage(sectionMessages.axes)}>
        <WsmFitmentSectionLinks current="axes" />
        <Button
          onClick={() => navigate(wsmFitmentAxisAddPath)}
          data-test-id="wsm-fitment-axis-create"
        >
          {intl.formatMessage(axisMessages.create)}
        </Button>
      </TopNav>
      <DashboardCard data-test-id="wsm-fitment-axis-list">
        <DashboardCard.Content paddingX={0}>
          {onRetry ? (
            <WsmFitmentLoadError onRetry={onRetry} />
          ) : (
            <ResponsiveTable>
              <TableHead>
                <TableRowLink>
                  <TableCell>{intl.formatMessage(axisMessages.colName)}</TableCell>
                  <TableCell>{intl.formatMessage(axisMessages.colDisplayLabel)}</TableCell>
                  <TableCell>{intl.formatMessage(axisMessages.colValueKind)}</TableCell>
                </TableRowLink>
              </TableHead>
              <TableBody>
                {renderCollection(
                  axes,
                  row => (
                    <TableRowLink
                      key={row ? row.id : "skeleton"}
                      hover={!!row}
                      href={row ? wsmFitmentAxisUrl(row.id) : undefined}
                      data-test-id="wsm-fitment-axis-row"
                    >
                      <TableCell>{row ? row.name : <Skeleton />}</TableCell>
                      <TableCell>{row ? row.displayLabel || "-" : <Skeleton />}</TableCell>
                      <TableCell>
                        {row ? (
                          intl.formatMessage(
                            row.valueKind === WsmFitmentValueKind.NUMBER
                              ? axisMessages.valueKindNumber
                              : axisMessages.valueKindText,
                          )
                        ) : (
                          <Skeleton />
                        )}
                      </TableCell>
                    </TableRowLink>
                  ),
                  () => (
                    <TableRowLink data-test-id="wsm-fitment-axis-list-empty">
                      <TableCell colSpan={numberOfColumns}>
                        <Text size={3} color="default2">
                          {intl.formatMessage(axisMessages.empty)}
                        </Text>
                      </TableCell>
                    </TableRowLink>
                  ),
                )}
              </TableBody>
            </ResponsiveTable>
          )}
        </DashboardCard.Content>
      </DashboardCard>
    </ListPageLayout>
  );
};

export default WsmFitmentAxisListPage;
